import React, { useMemo, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  BarChart3, Users, Target, Award, TrendingUp, TrendingDown, FileText, ChevronDown, ChevronUp,
} from 'lucide-react';

const pct = (score, total) => (total ? Math.round((score / total) * 100) : 0);
const fmt = (n) => (Number.isFinite(n) ? Math.round(n) : 0);

/**
 * TopicBankAnalytics — question-bank analytics for the teacher, computed from the
 * topic attempts (same spirit as the per-exam analytics page).
 */
const TopicBankAnalytics = ({ topics = [], attempts = [] }) => {
  const [openTopicId, setOpenTopicId] = useState(null);

  const passThreshold = (topicId) => {
    const t = topics.find((x) => x.id === topicId);
    return t?.passing_percentage ?? 50;
  };

  const overall = useMemo(() => {
    const finished = attempts.filter((a) => a.status === 'completed' || a.status === 'terminated');
    const students = new Set(finished.map((a) => a.student_name).filter(Boolean));
    const percents = finished.filter((a) => a.total_points).map((a) => pct(a.score, a.total_points));
    const avg = percents.length ? percents.reduce((s, x) => s + x, 0) / percents.length : 0;
    const passed = finished.filter((a) => a.total_points && pct(a.score, a.total_points) >= passThreshold(a.topic_id)).length;
    return {
      attempts: finished.length,
      students: students.size,
      avg,
      passRate: finished.length ? Math.round((passed / finished.length) * 100) : 0,
    };
  }, [attempts]); // eslint-disable-line react-hooks/exhaustive-deps

  const perTopic = useMemo(() => {
    const map = {};
    for (const a of attempts) {
      if (a.status !== 'completed' && a.status !== 'terminated') continue;
      const id = a.topic_id;
      if (!map[id]) map[id] = { topic_id: id, title: a.topic_title || '—', rows: [], students: new Set() };
      map[id].rows.push(a);
      if (a.student_name) map[id].students.add(a.student_name);
    }
    // include topics with zero attempts too
    for (const t of topics) {
      if (!map[t.id]) map[t.id] = { topic_id: t.id, title: t.title, rows: [], students: new Set() };
    }
    return Object.values(map).map((g) => {
      const percents = g.rows.filter((a) => a.total_points).map((a) => pct(a.score, a.total_points));
      const avg = percents.length ? percents.reduce((s, x) => s + x, 0) / percents.length : 0;
      const passed = g.rows.filter((a) => a.total_points && pct(a.score, a.total_points) >= passThreshold(g.topic_id)).length;
      return {
        ...g,
        attempts: g.rows.length,
        studentCount: g.students.size,
        avg,
        passRate: g.rows.length ? Math.round((passed / g.rows.length) * 100) : 0,
        high: percents.length ? Math.max(...percents) : null,
        low: percents.length ? Math.min(...percents) : null,
      };
    }).sort((a, b) => b.attempts - a.attempts);
  }, [attempts, topics]); // eslint-disable-line react-hooks/exhaustive-deps

  const studentRows = (group) => {
    const byStudent = {};
    for (const a of group.rows) {
      const k = a.student_name || '—';
      if (!byStudent[k]) byStudent[k] = { name: k, grade: a.student_grade, section: a.student_section, attempts: 0, best: 0 };
      byStudent[k].attempts += 1;
      const p = pct(a.score, a.total_points);
      if (p > byStudent[k].best) byStudent[k].best = p;
    }
    return Object.values(byStudent).sort((a, b) => b.best - a.best);
  };

  const stat = (icon, label, value, color) => (
    <Card className="border-[#E8DFCF]">
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-[#412402]/50">{label}</p>
          <p className="text-2xl font-bold text-[#412402]">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>{icon}</div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4" dir="rtl">
      <div className="flex items-center gap-2 text-[#412402]">
        <BarChart3 className="w-5 h-5 text-amber-700" />
        <span className="font-bold">تحليلات بنك الأسئلة</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stat(<FileText className="w-5 h-5 text-blue-600" />, 'إجمالي المحاولات', overall.attempts, 'bg-blue-100')}
        {stat(<Users className="w-5 h-5 text-purple-600" />, 'عدد الطلاب', overall.students, 'bg-purple-100')}
        {stat(<Target className="w-5 h-5 text-amber-600" />, 'متوسط النسبة', `${fmt(overall.avg)}%`, 'bg-amber-100')}
        {stat(<Award className="w-5 h-5 text-green-600" />, 'نسبة النجاح', `${overall.passRate}%`, 'bg-green-100')}
      </div>

      <Card className="border-[#E8DFCF]">
        <CardContent className="p-0">
          {perTopic.length === 0 ? (
            <div className="py-10 text-center text-[#412402]/50 text-sm">لا توجد بيانات بعد.</div>
          ) : (
            <div className="divide-y divide-[#E8DFCF]">
              {perTopic.map((g) => {
                const isOpen = openTopicId === g.topic_id;
                return (
                  <div key={g.topic_id}>
                    <button
                      onClick={() => setOpenTopicId(isOpen ? null : g.topic_id)}
                      className="w-full flex items-center gap-3 p-3 text-right hover:bg-[#FAFAFA]"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#412402] truncate">{g.title}</p>
                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#412402]/50 mt-0.5">
                          <span>{g.attempts} محاولة</span><span>•</span>
                          <span>{g.studentCount} طالب</span>
                          {g.high != null && (<><span>•</span>
                            <span className="text-green-700 flex items-center gap-0.5"><TrendingUp className="w-3 h-3" />{g.high}%</span>
                            <span className="text-red-600 flex items-center gap-0.5"><TrendingDown className="w-3 h-3" />{g.low}%</span></>)}
                        </div>
                      </div>
                      <div className="w-28 shrink-0">
                        <div className="flex items-center justify-between text-[10px] text-[#412402]/50 mb-0.5">
                          <span>متوسط</span><span className="font-bold text-[#412402]">{fmt(g.avg)}%</span>
                        </div>
                        <Progress value={g.avg} className="h-1.5" />
                      </div>
                      <Badge className={`shrink-0 ${g.passRate >= 50 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        نجاح {g.passRate}%
                      </Badge>
                      {isOpen ? <ChevronUp className="w-4 h-4 text-[#412402]/40" /> : <ChevronDown className="w-4 h-4 text-[#412402]/40" />}
                    </button>
                    {isOpen && g.rows.length > 0 && (
                      <div className="px-3 pb-3 bg-[#FCFAF5]">
                        <div className="grid grid-cols-[1fr_80px_80px_70px] gap-2 px-2 py-1 text-[11px] font-semibold text-[#412402]/60 border-b border-[#E8DFCF]">
                          <div>الطالب</div><div>الصف</div><div>المحاولات</div><div>الأفضل</div>
                        </div>
                        {studentRows(g).map((s) => (
                          <div key={s.name} className="grid grid-cols-[1fr_80px_80px_70px] gap-2 px-2 py-1.5 text-sm border-b border-[#F2EADA]">
                            <div className="text-[#412402] truncate">{s.name}</div>
                            <div className="text-[#412402]/60 text-xs">{s.grade || '-'}{s.section ? `/${s.section}` : ''}</div>
                            <div className="text-[#412402]/60 text-xs">{s.attempts}</div>
                            <div className={`text-xs font-bold ${s.best >= 50 ? 'text-green-600' : 'text-red-600'}`}>{s.best}%</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TopicBankAnalytics;
